/** biome-ignore-all lint/performance/noNamespaceImport: THREE is required here */
"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import * as THREE from "three";
import "./color-bends.css";
const MAX_COLORS = 8;
const frag = `
#define MAX_COLORS ${MAX_COLORS}
uniform vec2 uCanvas;
uniform float uTime;
uniform float uSpeed;
uniform vec2 uRot;
uniform int uColorCount;
uniform vec3 uColors[MAX_COLORS];
uniform int uTransparent;
uniform float uScale;
uniform float uFrequency;
uniform float uWarpStrength;
uniform vec2 uPointer; // in NDC [-1,1]
uniform float uMouseInfluence;
uniform float uParallax;
uniform float uNoise;
varying vec2 vUv;

void main() {
  float t = uTime * uSpeed;
  vec2 p = vUv * 2.0 - 1.0;
  p += uPointer * uParallax * 0.1;
  vec2 rp = vec2(p.x * uRot.x - p.y * uRot.y, p.x * uRot.y + p.y * uRot.x);
  vec2 q = vec2(rp.x * (uCanvas.x / uCanvas.y), rp.y);
  q /= max(uScale, 0.0001);
  q /= 0.5 + 0.2 * dot(q, q);
  q += 0.2 * cos(t) - 7.56;
  vec2 toward = (uPointer - rp);
  q += toward * uMouseInfluence * 0.2;

    vec3 col = vec3(0.0);
    float a = 1.0;

    if (uColorCount > 0) {
      vec2 s = q;
      vec3 sumCol = vec3(0.0);
      float cover = 0.0;
      for (int i = 0; i < MAX_COLORS; ++i) {
            if (i >= uColorCount) break;
            s -= 0.01;
            vec2 r = sin(1.5 * (s.yx * uFrequency) + 2.0 * cos(s * uFrequency));
            float m0 = length(r + sin(5.0 * r.y * uFrequency - 3.0 * t + float(i)) / 4.0);
            float kBelow = clamp(uWarpStrength, 0.0, 1.0);
            float kMix = pow(kBelow, 0.3); // strong response across 0..1
            float gain = 1.0 + max(uWarpStrength - 1.0, 0.0); // allow >1 to amplify displacement
            vec2 disp = (r - s) * kBelow;
            vec2 warped = s + disp * gain;
            float m1 = length(warped + sin(5.0 * warped.y * uFrequency - 3.0 * t + float(i)) / 4.0);
            float m = mix(m0, m1, kMix);
            float w = 1.0 - exp(-6.0 / exp(6.0 * m));
            sumCol += uColors[i] * w;
            cover = max(cover, w);
      }
      col = clamp(sumCol, 0.0, 1.0);
      a = uTransparent > 0 ? cover : 1.0;
    } else {
        vec2 s = q;
        for (int k = 0; k < 3; ++k) {
            s -= 0.01;
            vec2 r = sin(1.5 * (s.yx * uFrequency) + 2.0 * cos(s * uFrequency));
            float m0 = length(r + sin(5.0 * r.y * uFrequency - 3.0 * t + float(k)) / 4.0);
            float kBelow = clamp(uWarpStrength, 0.0, 1.0);
            float kMix = pow(kBelow, 0.3);
            float gain = 1.0 + max(uWarpStrength - 1.0, 0.0);
            vec2 disp = (r - s) * kBelow;
            vec2 warped = s + disp * gain;
            float m1 = length(warped + sin(5.0 * warped.y * uFrequency - 3.0 * t + float(k)) / 4.0);
            float m = mix(m0, m1, kMix);
            col[k] = 1.0 - exp(-6.0 / exp(6.0 * m));
        }
        a = uTransparent > 0 ? max(max(col.r, col.g), col.b) : 1.0;
    }

    if (uNoise > 0.0001) {
      float n = fract(sin(dot(gl_FragCoord.xy + vec2(uTime), vec2(12.9898, 78.233))) * 43758.5453123);
      col += (n - 0.5) * uNoise;
      col = clamp(col, 0.0, 1.0);
    }

    vec3 rgb = (uTransparent > 0) ? col * a : col;
    gl_FragColor = vec4(rgb, a);
}
`;
const vert = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;
export default function ColorBends(t0) {
  const $ = _c(32);
  const {
    className,
    style,
    rotation: t1,
    speed: t2,
    colors: t3,
    transparent: t4,
    autoRotate: t5,
    scale: t6,
    frequency: t7,
    warpStrength: t8,
    mouseInfluence: t9,
    parallax: t10,
    noise: t11
  } = t0;
  const rotation = t1 === undefined ? 45 : t1;
  const speed = t2 === undefined ? 0.2 : t2;
  let t12;
  if ($[0] !== t3) {
    t12 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t12;
  } else {
    t12 = $[1];
  }
  const colors = t12;
  const transparent = t4 === undefined ? true : t4;
  const autoRotate = t5 === undefined ? 0 : t5;
  const scale = t6 === undefined ? 1 : t6;
  const frequency = t7 === undefined ? 1 : t7;
  const warpStrength = t8 === undefined ? 1 : t8;
  const mouseInfluence = t9 === undefined ? 1 : t9;
  const parallax = t10 === undefined ? 0.5 : t10;
  const noise = t11 === undefined ? 0.1 : t11;
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const rafRef = useRef(null);
  const materialRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const rotationRef = useRef(rotation);
  const autoRotateRef = useRef(autoRotate);
  let t13;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = new THREE.Vector2(0, 0);
    $[2] = t13;
  } else {
    t13 = $[2];
  }
  const pointerTargetRef = useRef(t13);
  let t14;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = new THREE.Vector2(0, 0);
    $[3] = t14;
  } else {
    t14 = $[3];
  }
  const pointerCurrentRef = useRef(t14);
  const pointerSmoothRef = useRef(8);
  let t15;
  let t16;
  if ($[4] !== frequency || $[5] !== mouseInfluence || $[6] !== noise || $[7] !== parallax || $[8] !== scale || $[9] !== speed || $[10] !== transparent || $[11] !== warpStrength) {
    t15 = () => {
      const container = containerRef.current;
      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      const geometry = new THREE.PlaneGeometry(2, 2);
      const uColorsArray = Array.from({
        length: MAX_COLORS
      }, _temp);
      const material = new THREE.ShaderMaterial({
        vertexShader: vert,
        fragmentShader: frag,
        uniforms: {
          uCanvas: {
            value: new THREE.Vector2(1, 1)
          },
          uTime: {
            value: 0
          },
          uSpeed: {
            value: speed
          },
          uRot: {
            value: new THREE.Vector2(1, 0)
          },
          uColorCount: {
            value: 0
          },
          uColors: {
            value: uColorsArray
          },
          uTransparent: {
            value: transparent ? 1 : 0
          },
          uScale: {
            value: scale
          },
          uFrequency: {
            value: frequency
          },
          uWarpStrength: {
            value: warpStrength
          },
          uPointer: {
            value: new THREE.Vector2(0, 0)
          },
          uMouseInfluence: {
            value: mouseInfluence
          },
          uParallax: {
            value: parallax
          },
          uNoise: {
            value: noise
          }
        },
        premultipliedAlpha: true,
        transparent: true
      });
      materialRef.current = material;
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);
      const renderer = new THREE.WebGLRenderer({
        antialias: false,
        powerPreference: "high-performance",
        alpha: true
      });
      rendererRef.current = renderer;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(0, transparent ? 0 : 1);
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";
      renderer.domElement.style.display = "block";
      if (container) {
        container.appendChild(renderer.domElement);
      }
      const clock = new THREE.Clock();
      const handleResize = () => {
        const w = container?.clientWidth || 1;
        const h = container?.clientHeight || 1;
        renderer.setSize(w, h, false);
        material.uniforms.uCanvas.value.set(w, h);
      };
      handleResize();
      if (typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", handleResize);
      } else {
        const ro = new ResizeObserver(handleResize);
        if (container) {
          ro.observe(container);
        }
        resizeObserverRef.current = ro;
      }
      const loop = () => {
        const dt = clock.getDelta();
        const elapsed = clock.elapsedTime;
        material.uniforms.uTime.value = elapsed;
        const deg = rotationRef.current % 360 + autoRotateRef.current * elapsed;
        const rad = deg * Math.PI / 180;
        const c = Math.cos(rad);
        const s = Math.sin(rad);
        material.uniforms.uRot.value.set(c, s);
        const cur = pointerCurrentRef.current;
        const tgt = pointerTargetRef.current;
        const amt = Math.min(1, dt * pointerSmoothRef.current);
        cur.lerp(tgt, amt);
        material.uniforms.uPointer.value.copy(cur);
        renderer.render(scene, camera);
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
      return () => {
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        } else {
          window.removeEventListener("resize", handleResize);
        }
        geometry.dispose();
        material.dispose();
        renderer.dispose();
        renderer.forceContextLoss();
        if (renderer.domElement && renderer.domElement.parentElement === container && container) {
          container.removeChild(renderer.domElement);
        }
      };
    };
    t16 = [frequency, mouseInfluence, noise, parallax, scale, speed, transparent, warpStrength];
    $[4] = frequency;
    $[5] = mouseInfluence;
    $[6] = noise;
    $[7] = parallax;
    $[8] = scale;
    $[9] = speed;
    $[10] = transparent;
    $[11] = warpStrength;
    $[12] = t15;
    $[13] = t16;
  } else {
    t15 = $[12];
    t16 = $[13];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[14] !== autoRotate || $[15] !== colors || $[16] !== frequency || $[17] !== mouseInfluence || $[18] !== noise || $[19] !== parallax || $[20] !== rotation || $[21] !== scale || $[22] !== speed || $[23] !== transparent || $[24] !== warpStrength) {
    t17 = () => {
      const material_0 = materialRef.current;
      const renderer_0 = rendererRef.current;
      if (!material_0) {
        return;
      }
      rotationRef.current = rotation;
      autoRotateRef.current = autoRotate;
      material_0.uniforms.uSpeed.value = speed;
      material_0.uniforms.uScale.value = scale;
      material_0.uniforms.uFrequency.value = frequency;
      material_0.uniforms.uWarpStrength.value = warpStrength;
      material_0.uniforms.uMouseInfluence.value = mouseInfluence;
      material_0.uniforms.uParallax.value = parallax;
      material_0.uniforms.uNoise.value = noise;
      const toVec3 = _temp2;
      const arr = (colors || []).filter(Boolean).slice(0, MAX_COLORS).map(toVec3);
      for (let i = 0; i < MAX_COLORS; i++) {
        const vec = material_0.uniforms.uColors.value[i];
        if (i < arr.length) {
          vec.copy(arr[i]);
        } else {
          vec.set(0, 0, 0);
        }
      }
      material_0.uniforms.uColorCount.value = arr.length;
      material_0.uniforms.uTransparent.value = transparent ? 1 : 0;
      if (renderer_0) {
        renderer_0.setClearColor(0, transparent ? 0 : 1);
      }
    };
    t18 = [rotation, autoRotate, speed, scale, frequency, warpStrength, mouseInfluence, parallax, noise, colors, transparent];
    $[14] = autoRotate;
    $[15] = colors;
    $[16] = frequency;
    $[17] = mouseInfluence;
    $[18] = noise;
    $[19] = parallax;
    $[20] = rotation;
    $[21] = scale;
    $[22] = speed;
    $[23] = transparent;
    $[24] = warpStrength;
    $[25] = t17;
    $[26] = t18;
  } else {
    t17 = $[25];
    t18 = $[26];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      const material_1 = materialRef.current;
      const container_0 = containerRef.current;
      if (!(material_1 && container_0)) {
        return;
      }
      const handlePointerMove = e => {
        const rect = container_0.getBoundingClientRect();
        const x = (e.clientX - rect.left) / (rect.width || 1) * 2 - 1;
        const y = -((e.clientY - rect.top) / (rect.height || 1) * 2 - 1);
        pointerTargetRef.current.set(x, y);
      };
      container_0.addEventListener("pointermove", handlePointerMove);
      return () => {
        container_0.removeEventListener("pointermove", handlePointerMove);
      };
    };
    t20 = [];
    $[27] = t19;
    $[28] = t20;
  } else {
    t19 = $[27];
    t20 = $[28];
  }
  useEffect(t19, t20);
  const t21 = `color-bends-container ${className}`;
  let t22;
  if ($[29] !== style || $[30] !== t21) {
    t22 = <div className={t21} ref={containerRef} style={style} />;
    $[29] = style;
    $[30] = t21;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  return t22;
}
function _temp2(hex) {
  const h_0 = hex.replace("#", "").trim();
  const v = h_0.length === 3 ? [Number.parseInt(h_0[0] + h_0[0], 16), Number.parseInt(h_0[1] + h_0[1], 16), Number.parseInt(h_0[2] + h_0[2], 16)] : [Number.parseInt(h_0.slice(0, 2), 16), Number.parseInt(h_0.slice(2, 4), 16), Number.parseInt(h_0.slice(4, 6), 16)];
  return new THREE.Vector3(v[0] / 255, v[1] / 255, v[2] / 255);
}
function _temp() {
  return new THREE.Vector3(0, 0, 0);
}